<template>
    <slot v-bind="{
        ...props,
        ...state,
        setUpcomingScroller,
    }" />
</template>

<script setup>
import { onBeforeMount, reactive } from 'vue';
import { format } from 'date-fns';
import { defaultsDeep, isArray, isString } from 'lodash';
import { flatten } from 'q-flat';

import zonedDate from '../utils/zoned-date';
import { initialiseScroller } from '../modules/scroller';

const now = zonedDate();

const RESULTS_PAGE_LENGTH = 25;
const UPCOMING_SCROLLER_LENGTH = 12;
const DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ssxxxxx";

const TYPES = {
    PERFORMANCE: 'performance',
    EVENT: 'event',
    WATCH_LISTEN: 'watch-listen',
    PRESS_RELEASE: 'press-release',
    ARTIST: 'artist',
    PIECE: 'piece',
    SERIES: 'series',
    SEASON: 'season',
}

const FILTERS = {
    ALL: {
        name: 'all',
        title: 'All',
        filters: {},
    },
    EVENTS: {
        name: 'events',
        title: 'Events',
        filters: {
            filters: {
                page: {
                    type: [ TYPES.PERFORMANCE ],
                },
            },
        },
    },
    ARTISTS: {
        name: 'artists',
        title: 'Artists',
        filters: {
            filters: {
                page: {
                    type: [ TYPES.ARTIST ],
                },
            },
        },
    },
    // PIECES: {
    //     name: 'pieces',
    //     title: 'Pieces',
    //     filters: {
    //         filters: {
    //             page: {
    //                 type: [ TYPES.PIECE ],
    //             },
    //         },
    //     },
    // },
    PRESS_RELEASES: {
        name: 'press-releases',
        title: 'Press Releases',
        filters: {
            filters: {
                page: {
                    type: [ TYPES.PRESS_RELEASE ],
                },
            },
        },
    },
    WATCH_LISTEN: {
        name: 'watch-listen',
        title: 'Watch & Listen',
        filters: {
            filters: {
                page: {
                    type: [ TYPES.WATCH_LISTEN ],
                },
            },
        },
    },
    INFORMATION: {
        name: 'information',
        title: 'Information',
        filters: {
            filters: {
                page: {
                    type: [ `!${TYPES.PERFORMANCE}`, `!${TYPES.ARTIST}`, /*`!${TYPES.PIECE}`,*/ `!${TYPES.PRESS_RELEASE}`, `!${TYPES.WATCH_LISTEN}` ],
                },
            },
        },
    },
}

const props = defineProps({
    engineKey: {
        type: String,
        required: true,
    },
    siteIdentifier: {
        type: String,
        required: true,
    },
    queryParam: {
        type: String,
        default: 'q',
    },
    pageParam: {
        type: String,
        default: 'p',
    },
    filterParam: {
        type: String,
        default: 'f',
    },
});

const searchParams = new URLSearchParams(window.location.search);

const state = reactive({
    isLoading: true,
    isActive: false,
    searchQuery: searchParams.get(props.queryParam) ?? '',
    suggestedQuery: null,
    suggestedQueryUrl: null,
    currentPage: parseInt(searchParams.get(props.pageParam, 10)) || 1,
    currentFilter: searchParams.get(props.filterParam) ?? FILTERS.ALL.name,
    totalResults: 0,
    results: [],
    upcomingResults: [],
    tabs: [],
    pagination: {
        previous: false,
        next: false,
        pages: [],
    },
});

let upcomingScroller;

const swiftypeUrl = params => {
    let allParams = defaultsDeep({}, params, {
        q: state.searchQuery,
        spelling: 'always',
        engine_key: props.engineKey,
        page: state.currentPage,
        filters: {
            page: {
                site: props.siteIdentifier,
            },
        },
    });

    allParams = flatten(allParams);

    const url = new URL('https://api.swiftype.com/api/v1/public/engines/search.json');

    for (const [ key, value ] of Object.entries(allParams)) {
        url.searchParams.append(key.replace(/\[\d+\]/, '[]'), value);
    }

    return url.toString();
}

const searchPageUrl = (page, query) => {
    const url = new URL(window.location);

    if (query) {
        url.searchParams.set(props.queryParam, query);
    }

    url.searchParams.set(props.pageParam, page);

    return url.toString();
}

const searchFilterUrl = (filter, page) => {
    const url = new URL(window.location);

    url.searchParams.set(props.filterParam, filter);

    if (page) {
        url.searchParams.set('p', page);
    }

    return url.toString();
}

const parseDescription = page => {
    if (page.highlight && page.highlight.body) {
        return page.highlight.body;
    }

    return page.info;
}

const friendlyType = (type) => {
    return type
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const handleSearchResults = async () => {
    let params = {
        per_page: RESULTS_PAGE_LENGTH,
        highlight_fields: {
            page: {
                body: {
                    size: 300,
                    fallback: true,
                },
            },
        },
    }

    // THIS COMMA IS ON PURPOSE, REMOVE IT YOU BREAK EVERYTHING!
    for (const [ , config ] of Object.entries(FILTERS)) {
        if (state.currentFilter === config.name) {
            params = defaultsDeep({}, params, config.filters);
        }
    }

    const response = await fetch(swiftypeUrl(params)).then(response => { // eslint-disable-line compat/compat
        return response.json();
    });

    const suggestion = response?.info?.page?.spelling_suggestion?.text;

    if (suggestion) {
        state.suggestedQuery = suggestion;
        state.suggestedQueryUrl = searchPageUrl(1, suggestion);
    }

    state.totalResults = response.info.page.total_result_count;

    const currentPage = response.info.page.current_page;
    const totalPages = response.info.page.num_pages;

    if (currentPage === 1) {
        state.pagination.previous = false;
    } else {
        state.pagination.previous = searchPageUrl(currentPage - 1);
    }

    if (currentPage === totalPages) {
        state.pagination.next = false;
    } else {
        state.pagination.next = searchPageUrl(currentPage + 1);
    }

    state.pagination.pages = [];

    if (totalPages <= 6) {
        for (let page = 1; page <= totalPages; page++) {
            state.pagination.pages.push({
                url: searchPageUrl(page),
                title: page,
                isCurrent: currentPage === page,
            });
        }
    } else {
        state.pagination.pages.push({
            url: searchPageUrl(1),
            title: 1,
            isCurrent: currentPage === 1,
        });

        state.pagination.pages.push({
            url: searchPageUrl(2),
            title: 2,
            isCurrent: currentPage === 2,
        });

        state.pagination.pages.push({
            url: searchPageUrl(3),
            title: 3,
            isCurrent: currentPage === 3,
        });

        if (currentPage > 5) {
            state.pagination.pages.push(null);
        }

        for (let page = currentPage - 1; page <= currentPage + 1; page++) {
            if (page > 3 && page < totalPages - 2) {
                state.pagination.pages.push({
                    url: searchPageUrl(page),
                    title: page,
                    isCurrent: page === currentPage,
                });
            }
        }

        if (currentPage < (totalPages - 4)) {
            state.pagination.pages.push(null);
        }

        state.pagination.pages.push({
            url: searchPageUrl(totalPages - 2),
            title: totalPages - 2,
            isCurrent: currentPage === (totalPages - 2),
        });

        state.pagination.pages.push({
            url: searchPageUrl(totalPages - 1),
            title: totalPages - 1,
            isCurrent: currentPage === (totalPages - 1),
        });

        state.pagination.pages.push({
            url: searchPageUrl(totalPages),
            title: totalPages,
            isCurrent: currentPage === totalPages,
        });
    }

    state.results = response.records.page.map(page => {
        const result = {
            ...page,
            isBasker: page.cms == 'basker',
            displayType: friendlyType(page.type),
            url: page.url.replace(/^(\w+:\/\/[\w.]+\/)/, '/'),
            title: page.internal_title || page.title,
        }

        if (page.type === TYPES.PERFORMANCE) {
            result.start_date = zonedDate(page.start_date);

            if (result.start_date > now) {
                result.displayType = 'Upcoming Event';
            } else {
                result.displayType = 'Past Event';
            }
        } else if (page.type === TYPES.PRESS_RELEASE || page.type === TYPES.WATCH_LISTEN) {
            if ('publish_date' in page) {
                result.publish_date = format(zonedDate(page.publish_date), 'MMMM d, yyyy');
            }

            if ('date' in page) {
                result.publish_date = format(zonedDate(page.date), 'MMMM d, yyyy');
            }
        }

        if (!result.isBasker) {
            result.image = page.internal_image;
        } else if (isArray(page.image) && page.image.length) {
            result.image = page.image[0];
        } else if (isString(page.image) && page.image) {
            result.image = page.image;
        }

        result.description = page.highlight.body;

        return result;
    });
}

const handleUpcomingResults = async () => {
    const params = {
        per_page: UPCOMING_SCROLLER_LENGTH,
        sort_field: {
            page: 'start_date',
        },
        sort_direction: {
            page: 'asc',
        },
        filters: {
            page: {
                start_date: {
                    type: 'range',
                    from: format(now, DATETIME_FORMAT),
                },
            },
        },
    }

    const response = await fetch(swiftypeUrl(params)).then(response => { // eslint-disable-line compat/compat
        return response.json();
    });

    state.upcomingResults = response.records.page.map(page => {
        const result = {
            ...page,
            isBasker: page.cms == 'basker',
            displayType: friendlyType(page.type),
            url: page.url.replace(/^(\w+:\/\/[\w.]+\/)/, '/'),
            start_date: zonedDate(page.start_date),
            title: page.internal_title || page.title,
        }

        if (!result.isBasker) {
            result.image = page.internal_image;
        } else if (isArray(page.image) && page.image.length) {
            result.image = page.image[0];
        } else if (isString(page.image) && page.image) {
            result.image = page.image;
        }

        return result;
    });
}

const getCountResults = async filter => {
    let params = {
        per_page: 1,
        page: 1,
    }

    // THIS COMMA IS ON PURPOSE, REMOVE IT YOU BREAK EVERYTHING!
    for (const [ , config ] of Object.entries(FILTERS)) {
        if (filter === config.name) {
            params = defaultsDeep({}, params, config.filters);
        }
    }

    return fetch(swiftypeUrl(params)).then(response => { // eslint-disable-line compat/compat
        return response.json();
    });
}

const generateTabs = async () => {
    const searchParams = new URLSearchParams(window.location.search);

    const allowedTabs = [];

    for (const [ key, config ] of Object.entries(FILTERS)) {
        if (key === 'ALL') {
            continue;
        }

        allowedTabs.push({
            name: config.name,
            title: config.title,
            url: searchFilterUrl(config.name, 1),
            isActive: searchParams.get(props.filterParam) === config.name,
        });
    }

    const promises = allowedTabs.map(tab => {
        return getCountResults(tab.name);
    });

    return Promise.all(promises).then(responses => {
        const tabs = [];

        for (let i = 0; i < allowedTabs.length; i++) {
            if (responses[i].record_count) {
                tabs.push(allowedTabs[i]);
            }
        }

        tabs.unshift({
            name: FILTERS.ALL.name,
            title: FILTERS.ALL.title,
            url: searchFilterUrl(FILTERS.ALL.name, 1),
            isActive: !searchParams.has(props.filterParam) || searchParams.get(props.filterParam) === FILTERS.ALL.name,
        });

        state.tabs = tabs;
    });
}

const doSearch = async () => {
    state.isLoading = true;
    state.upcomingResults = [];

    // Normal searches
    await handleSearchResults();

    if ([FILTERS.ALL.name, FILTERS.EVENTS.name].includes(state.currentFilter) && state.currentPage === 1) {
        await handleUpcomingResults();
    }

    // Generate tabs
    await generateTabs();

    state.isLoading = false;
    state.isActive = true;

    requestAnimationFrame(() => {
        if (upcomingScroller) {
            initialiseScroller(upcomingScroller);
        }
    });
}

const setUpcomingScroller = element => {
    upcomingScroller = element;
}

onBeforeMount(() => {
    if (state.searchQuery) {
        doSearch();
    } else {
        state.isLoading = false;
    }
});
</script>
